import { initializeApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, connectAuthEmulator } from 'firebase/auth'
import {
  initializeFirestore,
  CACHE_SIZE_UNLIMITED,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator
} from 'firebase/firestore'
import { connectStorageEmulator, getStorage } from 'firebase/storage'
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider
} from 'firebase/app-check'
import { getVertexAI, getGenerativeModel } from '@firebase/vertexai'
import { getPerformance } from 'firebase/performance'

const usingEmulators =
  process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true' ||
  process.env.NODE_ENV === 'test'

function requiredConfig(name: string, emulatorFallback: string): string {
  const value = process.env[name]
  if (value) return value
  if (usingEmulators) return emulatorFallback
  throw new Error(`Missing required Firebase configuration: ${name}`)
}

export const firebaseConfig = {
  apiKey: requiredConfig('REACT_APP_FIREBASE_API_KEY', 'demo-api-key'),
  authDomain: requiredConfig(
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'clinic-web-app.firebaseapp.com'
  ),
  projectId: requiredConfig('REACT_APP_FIREBASE_PROJECT_ID', 'clinic-web-app'),
  storageBucket: requiredConfig(
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'clinic-web-app.appspot.com'
  ),
  messagingSenderId: requiredConfig(
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    '000000000000'
  ),
  appId: requiredConfig('REACT_APP_FIREBASE_APP_ID', '1:000000000000:web:demo'),
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

if (!usingEmulators) {
  if (
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_APPCHECK_DEBUG === 'true'
  ) {
    Object.assign(globalThis, { FIREBASE_APPCHECK_DEBUG_TOKEN: true })
  }
  const recaptchaKey = requiredConfig(
    'REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY',
    'unused-in-emulator-mode'
  )
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
    isTokenAutoRefreshEnabled: true
  })
}

// Initialize Cloud Firestore and get a reference to the service.
// A Firestore restore always lands in a NEW database - there is no
// restore-into-(default) - so the database id is a variable rather than an
// implicit default. Recovery is then a repository-variable flip instead of a
// code edit under pressure. See docs/ops/backup-and-restore.md.
const databaseId = process.env.REACT_APP_FIREBASE_DATABASE_ID ?? '(default)'
const db = initializeFirestore(
  app,
  {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
      cacheSizeBytes: CACHE_SIZE_UNLIMITED
    })
  },
  databaseId
)
const storage = getStorage(app)
const auth = getAuth()
const functionsRegion =
  process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION ?? 'europe-west1'
const functions = getFunctions(app, functionsRegion)

// Emulator ports are overridable so several worktrees can run their own suite
// side by side; scripts/dev-ports.js allocates them. The fallbacks are the
// firebase.json defaults, so an unset environment behaves as before.
const emulatorHost = process.env.REACT_APP_EMULATOR_HOST ?? 'localhost'

function emulatorPort(name: string, fallback: number): number {
  const parsed = Number(process.env[`REACT_APP_EMULATOR_${name}_PORT`])
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

if (usingEmulators) {
  connectAuthEmulator(
    auth,
    `http://${emulatorHost}:${emulatorPort('AUTH', 9099)}`,
    { disableWarnings: true }
  )
  connectFirestoreEmulator(db, emulatorHost, emulatorPort('FIRESTORE', 8080))
  connectStorageEmulator(storage, emulatorHost, emulatorPort('STORAGE', 9199))
  connectFunctionsEmulator(
    functions,
    emulatorHost,
    emulatorPort('FUNCTIONS', 5001)
  )
}

const googleAuthProvider = new GoogleAuthProvider()

if (!usingEmulators) {
  getPerformance(app)
}

const vertexAI = !usingEmulators
  ? getVertexAI(app, { location: 'global' })
  : (null as any)
const model = !usingEmulators
  ? getGenerativeModel(vertexAI, { model: 'gemini-3.5-flash' })
  : (null as any)

export { app, db, storage, auth, functions, googleAuthProvider, model }
